import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  AriaDatePickerFixedRangePreset,
  AriaDatePickerFormValue,
  AriaDatePickerModelFormat,
  AriaDatePickerPeriod,
  AriaDatePickerRangeConstraint,
  AriaDatePickerSelection,
} from '../models/aria-date-picker.model';

dayjs.extend(isoWeek);
dayjs.extend(isBetween);
dayjs.extend(duration);

export const ARIA_DATE_DISPLAY_FORMAT = 'DD/MM/YY';
export const ARIA_DATE_ISO_FORMAT = 'YYYY-MM-DD';

const WEEKDAY_LABELS_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export interface AriaCalendarDay {
  key: string;
  date: Dayjs;
  label: number;
  inMonth: boolean;
  disabled: boolean;
  selected: boolean;
  inRange: boolean;
  rangeStart: boolean;
  rangeEnd: boolean;
  today: boolean;
  /** Cella vuota al posto di un giorno fuori mese già mostrato nel pannello adiacente. */
  hidden?: boolean;
}

export interface AriaCalendarWeek {
  id: string;
  days: AriaCalendarDay[];
}

export function weekdayLabels(): string[] {
  return [...WEEKDAY_LABELS_IT];
}

export function toDay(value: Date | string | Dayjs | null | undefined): Dayjs | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf('day') : null;
}

export function computePeriodEnd(start: Dayjs, period: AriaDatePickerPeriod): Dayjs {
  switch (period) {
    case 'semestrale':
      return start.add(6, 'month').subtract(1, 'day');
    case 'mensile':
      return start.add(1, 'month').subtract(1, 'day');
    case 'settimanale':
      return start.add(7, 'day').subtract(1, 'day');
    case 'annual':
    default:
      return start.add(1, 'year').subtract(1, 'day');
  }
}

export function computeFixedRangeEnd(
  start: Dayjs,
  preset: AriaDatePickerFixedRangePreset
): Dayjs {
  if (preset === 3) {
    return start.add(2, 'day');
  }
  if (preset === 15) {
    return start.add(14, 'day');
  }
  return start.add(12, 'month').subtract(1, 'day');
}

/**
 * Calcola `maxDate` dinamico dopo la selezione della data inizio (logica EA legacy).
 */
export function computeDynamicMaxDate(
  start: Dayjs,
  constraint: AriaDatePickerRangeConstraint
): Date | undefined {
  if (constraint.maxDateOneMonth) {
    return start.add(31, 'day').toDate();
  }

  const maxDays = constraint.maxDays;
  if (!maxDays || maxDays <= 0) {
    return undefined;
  }

  if (maxDays === 59) {
    const nextMonth = start.add(2, 'month');
    const finish = nextMonth.subtract(1, 'day');
    return start.add(dayjs.duration(finish.diff(start)).as('days'), 'day').toDate();
  }

  if (maxDays === 30) {
    const finish = start.add(1, 'month').subtract(1, 'day');
    return start.add(dayjs.duration(finish.diff(start)).as('days'), 'day').toDate();
  }

  if (maxDays === 29) {
    return start.add(29, 'day').toDate();
  }

  if (maxDays === 179) {
    const finish = start.add(6, 'month').subtract(1, 'day');
    return start.add(Math.trunc(dayjs.duration(finish.diff(start)).as('days')), 'day').toDate();
  }

  return start.add(maxDays - 1, 'day').toDate();
}

export function isDateSelectable(
  date: Dayjs,
  minDate: Dayjs | null,
  maxDate: Dayjs | null
): boolean {
  if (minDate && date.isBefore(minDate, 'day')) {
    return false;
  }
  if (maxDate && date.isAfter(maxDate, 'day')) {
    return false;
  }
  return true;
}

export function buildCalendarWeeks(
  viewMonth: Dayjs,
  selection: AriaDatePickerSelection | null,
  minDate: Dayjs | null,
  maxDate: Dayjs | null,
  rangePreviewEnd?: Dayjs | null,
  hideOutsideDaysInMonth?: Dayjs | null,
  enableRangePreview = false,
): AriaCalendarWeek[] {
  const start = viewMonth.startOf('month').startOf('isoWeek');
  const end = viewMonth.endOf('month').endOf('isoWeek');
  const weeks: AriaCalendarWeek[] = [];
  let cursor = start;
  let weekIndex = 0;

  const hasCommittedRange = !!selection?.start && !!selection?.end;
  const hasPreviewRange =
    enableRangePreview && !!selection?.start && !selection?.end && !!rangePreviewEnd;
  const preview = hasPreviewRange
    ? normalizeRangeSelection(selection!.start, rangePreviewEnd!)
    : null;

  const rangeStartDay = hasCommittedRange
    ? selection!.start
    : hasPreviewRange
      ? preview!.start
      : null;
  const rangeEndDay = hasCommittedRange
    ? selection!.end!
    : hasPreviewRange
      ? preview!.end!
      : null;
  const singleSelectedDay =
    !hasCommittedRange && !hasPreviewRange && selection?.start ? selection.start : null;

  while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
    const days: AriaCalendarDay[] = [];

    for (let i = 0; i < 7; i++) {
      const inMonth = cursor.month() === viewMonth.month();
      const hideAsDuplicate =
        !inMonth &&
        hideOutsideDaysInMonth != null &&
        cursor.isSame(hideOutsideDaysInMonth, 'month');

      if (hideAsDuplicate) {
        days.push({
          key: `empty-${weekIndex}-${i}`,
          date: cursor,
          label: cursor.date(),
          inMonth: false,
          disabled: true,
          hidden: true,
          selected: false,
          inRange: false,
          rangeStart: false,
          rangeEnd: false,
          today: false,
        });
        cursor = cursor.add(1, 'day');
        continue;
      }

      const disabled = !isDateSelectable(cursor, minDate, maxDate);
      let rangeStart = !!rangeStartDay && cursor.isSame(rangeStartDay, 'day');
      let rangeEnd = !!rangeEndDay && cursor.isSame(rangeEndDay, 'day');
      const singleSelected = !!singleSelectedDay && cursor.isSame(singleSelectedDay, 'day');

      if (singleSelected) {
        rangeStart = true;
        rangeEnd = true;
      }
      const inCommittedRange =
        hasCommittedRange &&
        cursor.isAfter(selection!.start, 'day') &&
        cursor.isBefore(selection!.end!, 'day');

      const inPreviewRange =
        hasPreviewRange &&
        !!rangeEndDay &&
        cursor.isAfter(rangeStartDay!, 'day') &&
        cursor.isBefore(rangeEndDay, 'day');

      days.push({
        key: cursor.format(ARIA_DATE_ISO_FORMAT),
        date: cursor,
        label: cursor.date(),
        inMonth,
        disabled,
        selected: rangeStart || rangeEnd,
        inRange: inCommittedRange || inPreviewRange,
        rangeStart,
        rangeEnd,
        today: cursor.isSame(dayjs(), 'day'),
      });
      cursor = cursor.add(1, 'day');
    }

    weeks.push({ id: `week-${weekIndex}`, days });
    weekIndex += 1;
  }

  return weeks;
}

export function isDayInCalendarGrid(
  viewMonth: Dayjs,
  dayKey: string,
  selection: AriaDatePickerSelection | null,
  minDate: Dayjs | null,
  maxDate: Dayjs | null,
  rangePreviewEnd?: Dayjs | null,
  hideOutsideDaysInMonth?: Dayjs | null,
  enableRangePreview = false,
): boolean {
  const weeks = buildCalendarWeeks(
    viewMonth,
    selection,
    minDate,
    maxDate,
    rangePreviewEnd,
    hideOutsideDaysInMonth,
    enableRangePreview,
  );
  return weeks.some((week) => week.days.some((day) => !day.hidden && day.key === dayKey));
}

export function formatDisplayValue(
  selection: AriaDatePickerSelection | null,
  displayFormat = ARIA_DATE_DISPLAY_FORMAT
): string {
  if (!selection?.start) {
    return '';
  }
  if (!selection.end) {
    return selection.start.format(displayFormat);
  }
  return `${selection.start.format(displayFormat)} - ${selection.end.format(displayFormat)}`;
}

export function selectionToFormValue(
  selection: AriaDatePickerSelection | null,
  modelFormat: AriaDatePickerModelFormat,
  isRangeLike: boolean
): AriaDatePickerFormValue {
  if (!selection?.start) {
    return null;
  }

  if (!isRangeLike || !selection.end) {
    if (modelFormat === 'dayjs') {
      return selection.start;
    }
    return selection.start.format(ARIA_DATE_ISO_FORMAT);
  }

  if (modelFormat === 'dayjs') {
    return [selection.start, selection.end];
  }
  return [
    selection.start.format(ARIA_DATE_ISO_FORMAT),
    selection.end.format(ARIA_DATE_ISO_FORMAT),
  ];
}

export function formValueToSelection(
  value: AriaDatePickerFormValue,
  modelFormat: AriaDatePickerModelFormat
): AriaDatePickerSelection | null {
  if (value == null || value === '') {
    return null;
  }

  if (Array.isArray(value)) {
    const start = modelFormat === 'dayjs' ? toDay(value[0] as Dayjs) : toDay(value[0] as string);
    const end = modelFormat === 'dayjs' ? toDay(value[1] as Dayjs) : toDay(value[1] as string);
    if (!start) {
      return null;
    }
    return { start, end: end ?? undefined };
  }

  const single = modelFormat === 'dayjs' ? toDay(value as Dayjs) : toDay(value as string);
  return single ? { start: single } : null;
}

export function normalizeRangeSelection(start: Dayjs, end: Dayjs): AriaDatePickerSelection {
  if (end.isBefore(start, 'day')) {
    return { start: end, end: start };
  }
  return { start, end };
}

export interface AriaPickerMonthCell {
  key: string;
  date: Dayjs;
  label: string;
  disabled: boolean;
  selected: boolean;
  today: boolean;
}

export interface AriaPickerYearCell {
  key: string;
  date: Dayjs;
  label: string;
  disabled: boolean;
  selected: boolean;
  today: boolean;
}

export function buildMonthCells(
  viewYear: Dayjs,
  selection: AriaDatePickerSelection | null,
  minDate: Dayjs | null,
  maxDate: Dayjs | null
): AriaPickerMonthCell[] {
  const months: AriaPickerMonthCell[] = [];
  for (let month = 0; month < 12; month++) {
    const date = viewYear.month(month).startOf('month');
    const disabled =
      (minDate && date.endOf('month').isBefore(minDate, 'day')) ||
      (maxDate && date.startOf('month').isAfter(maxDate, 'day')) ||
      false;
    const selected =
      !!selection?.start &&
      selection.start.year() === date.year() &&
      selection.start.month() === date.month() &&
      !selection.end;

    months.push({
      key: date.format('YYYY-MM'),
      date,
      label: date.format('MMM'),
      disabled,
      selected,
      today: date.isSame(dayjs(), 'month'),
    });
  }
  return months;
}

export function buildYearCells(
  viewYear: Dayjs,
  selection: AriaDatePickerSelection | null,
  minDate: Dayjs | null,
  maxDate: Dayjs | null
): AriaPickerYearCell[] {
  const startYear = viewYear.year() - 5;
  const years: AriaPickerYearCell[] = [];
  for (let i = 0; i < 12; i++) {
    const date = dayjs().year(startYear + i).startOf('year');
    const disabled =
      (minDate && date.endOf('year').isBefore(minDate, 'day')) ||
      (maxDate && date.startOf('year').isAfter(maxDate, 'day')) ||
      false;
    const selected = !!selection?.start && selection.start.year() === date.year() && !selection.end;

    years.push({
      key: date.format('YYYY'),
      date,
      label: date.format('YYYY'),
      disabled,
      selected,
      today: date.isSame(dayjs(), 'year'),
    });
  }
  return years;
}

export function initialPanelView(
  granularity: 'day' | 'month' | 'year',
  enableYearMonthNavigation: boolean
): 'days' | 'months' | 'years' {
  if (granularity === 'year') {
    return 'years';
  }
  if (granularity === 'month') {
    return 'months';
  }
  return enableYearMonthNavigation ? 'days' : 'days';
}

export function normalizeByGranularity(date: Dayjs, granularity: 'day' | 'month' | 'year'): Dayjs {
  if (granularity === 'year') {
    return date.startOf('year');
  }
  if (granularity === 'month') {
    return date.startOf('month');
  }
  return date.startOf('day');
}
